'use client'

import {
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useState,
  useSyncExternalStore,
} from 'react'

import { useOnboardingExit } from './OnboardingExitContext'
import {
  captureOnboardingCheckoutReturnFromUrl,
  peekOnboardingCheckoutReturn,
  subscribeOnboardingCheckoutReturn,
} from './onboarding-checkout-return'
import { useOnboardingGate } from './useOnboardingGate'
import { ONBOARDING_PATH } from './constants'
import {
  resolveOnboardingRouteGateAction,
  shouldShowOnboardingFallback,
} from './onboardingRouteGate.logic'
import { usePathname, useRouter } from '@/navigation'

type OnboardingRouteGateProps = {
  children: ReactNode
  fallback: ReactNode
}

export const OnboardingRouteGate = ({
  children,
  fallback,
}: OnboardingRouteGateProps) => {
  const pathname = usePathname()
  const router = useRouter()
  const gate = useOnboardingGate()
  const { exitHref, endExit } = useOnboardingExit()
  const hasPendingCheckoutReturn = useSyncExternalStore(
    subscribeOnboardingCheckoutReturn,
    () => peekOnboardingCheckoutReturn() != null,
    () => false,
  )

  useLayoutEffect(() => {
    captureOnboardingCheckoutReturnFromUrl()
  }, [])

  const isOnboardingRoute =
    pathname === ONBOARDING_PATH || pathname.startsWith(`${ONBOARDING_PATH}/`)

  const [onboardingLatch, setOnboardingLatch] = useState(false)

  useEffect(() => {
    if (!isOnboardingRoute) return

    if (gate.gateResolved && (gate.isRequired || exitHref != null)) {
      setOnboardingLatch(true)
      return
    }

    if (gate.gateResolved && !gate.isRequired && exitHref == null) {
      setOnboardingLatch(false)
    }
  }, [exitHref, gate.gateResolved, gate.isRequired, isOnboardingRoute])

  useEffect(() => {
    const action = resolveOnboardingRouteGateAction({
      gate,
      pathname,
      exitHref,
    })

    switch (action.type) {
      case 'replace':
        router.replace(action.href)
        break
      case 'endExit':
        endExit()
        break
      case 'noop':
        break
    }
  }, [endExit, exitHref, gate, pathname, router])

  if (isOnboardingRoute) {
    if (!onboardingLatch && !gate.gateResolved && !hasPendingCheckoutReturn) {
      return <>{fallback}</>
    }
    if (gate.gateResolved && !gate.isRequired && exitHref == null) {
      return <>{fallback}</>
    }
    return <>{children}</>
  }

  if (
    shouldShowOnboardingFallback({
      gate,
      pathname,
      exitHref,
    })
  ) {
    return <>{fallback}</>
  }

  return <>{children}</>
}

export function shouldRenderFullAppShell(pathname: string): boolean {
  return !(
    pathname === ONBOARDING_PATH || pathname.startsWith(`${ONBOARDING_PATH}/`)
  )
}
